import { useState } from "react";
import { Check, ChevronDown } from "lucide-react";
import { TICKET_POINTS, PACKAGES, PIGULKI, PROMO_DEADLINE_TEXT, REGULAMIN_URL } from "../content";
import { usePromotion } from "../hooks/usePromotion";
import { track, trackCustom } from "../analytics/metaPixel";
import Countdown from "./Countdown";
import BuyButton from "./BuyButton";
import Reveal from "./Reveal";

const PIGULKI_BY_ID = Object.fromEntries(PIGULKI.map((p) => [p.id, p]));

const priceValue = (price) => Number.parseFloat(price.replace(/\s*zł\s*/u, "").replace(",", "."));

const trackPackageClick = (pkg) => {
  trackCustom("ClickCTA", { location: pkg.id });
  track("InitiateCheckout", {
    content_name: pkg.name,
    value: priceValue(pkg.price),
    currency: "PLN",
  });
};

function PricePromo({ regular, price, note }) {
  return (
    <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2">
      <del className="text-4xl font-extrabold tracking-tight text-[#34343F] decoration-[#FF008B] decoration-4 sm:text-5xl">
        {regular}
      </del>
      <span className="rounded-2xl bg-[#FDF4F8] px-3.5 py-1.5 text-xl font-extrabold tracking-tight text-[#FF008B] sm:text-2xl">
        {price} <span className="text-sm font-bold">{note}</span>
      </span>
    </div>
  );
}

function PackageItem({ item, pkgId }) {
  const [open, setOpen] = useState(false);
  const pigulka = item.pigulka ? PIGULKI_BY_ID[item.pigulka] : null;
  const panelId = `pkg-${pkgId}-pigulka-${item.pigulka}`;

  if (!pigulka) {
    return (
      <li className="flex items-start gap-2.5 text-sm font-medium text-[#34343F]">
        <Check size={16} className="mt-0.5 shrink-0 text-[#FF008B]" aria-hidden="true" />
        {item.label}
      </li>
    );
  }

  return (
    <li className="text-sm font-medium text-[#34343F]">
      <div className="flex items-start gap-2.5">
        <Check size={16} className="mt-0.5 shrink-0 text-[#FF008B]" aria-hidden="true" />
        <span>{item.label}</span>
      </div>
      <button
        type="button"
        data-testid={`package-pigulka-more-${pkgId}-${item.pigulka}`}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls={panelId}
        className="ml-6 mt-1 inline-flex min-h-[36px] items-center gap-1.5 rounded-full border border-[#FFB3DA] bg-[#FDF4F8] px-3.5 py-1 text-xs font-bold text-[#FF008B] transition-colors duration-200 hover:bg-[#FF008B] hover:text-white"
      >
        {open ? "Zwiń program" : "Dowiedz się więcej"}
        <ChevronDown
          size={14}
          aria-hidden="true"
          className={`transition-transform duration-300 ${open ? "rotate-180" : ""}`}
        />
      </button>
      <div
        id={panelId}
        className="acc-panel"
        data-open={open}
        role="region"
        aria-label={`Program: ${pigulka.title}`}
      >
        <div>
          <div className="ml-6 mt-2 rounded-xl border border-[#F5E3B8] bg-[#FFFBEC] p-4">
            <p className="text-xs font-bold uppercase tracking-wide text-[#C08400]">
              Pigułka Wiedzy · 10 lekcji · prowadząca: {pigulka.trainer}
            </p>
            <ol className="mt-2.5 space-y-2">
              {pigulka.lessons.map((lesson, i) => (
                <li key={i} className="text-xs leading-relaxed text-[#34343F]">
                  <span className="font-bold text-[#FF008B]">Lekcja {i + 1}. </span>
                  {lesson}
                </li>
              ))}
            </ol>
          </div>
        </div>
      </div>
    </li>
  );
}

function SoloCard({ active }) {
  return (
    <div
      data-testid="final-offer-card"
      className="flex h-full flex-col overflow-hidden rounded-3xl border border-[#EAE6F0] bg-white shadow-xl shadow-[#242344]/5 transition-all duration-300 hover:-translate-y-2 hover:shadow-2xl hover:shadow-[#FF008B]/20 hover:ring-2 hover:ring-[#FFB3DA]"
    >
      <div className="airmail-edge" aria-hidden="true" />
      <div className="bg-[#FAF8FC] p-6">
        <img
          src="/assets/keyvisual.png"
          alt="Okładka kursu „Umiem uczyć angielskiego”"
          loading="lazy"
          width="1024"
          height="1024"
          className="mx-auto w-full max-w-[16rem] rounded-2xl shadow-md"
        />
      </div>
      <div className="flex flex-1 flex-col p-7">
        <p className="text-center text-xs font-extrabold uppercase tracking-[0.25em] text-[#FF008B]">
          Bilet online
        </p>
        <h3 className="mt-2 text-center text-xl font-bold text-[#242344]">
          Sam kurs „Umiem Uczyć Angielskiego”
        </h3>
        <ul className="mx-auto mt-5 w-fit space-y-2.5">
          {TICKET_POINTS.map((point) => (
            <li key={point} className="flex items-center gap-2.5 text-sm font-medium text-[#34343F]">
              <Check size={16} className="shrink-0 text-[#FF008B]" aria-hidden="true" />
              {point}
            </li>
          ))}
        </ul>

        {active ? (
          <div className="mt-6 text-center">
            <PricePromo regular="399 zł" price="249 zł" note="brutto" />
            <p className="mt-1 text-xs font-semibold text-[#68677A]">
              cena regularna / cena promocyjna
            </p>
            <p className="mt-2.5 text-sm font-bold text-[#242344]">Oszczędzasz 150 zł</p>
            <p className="mt-4 text-xs font-semibold text-[#242344]">
              Tylko do 20 października 2026, do północy
            </p>
            <span className="sr-only">{PROMO_DEADLINE_TEXT}</span>
            <div className="mt-2.5 flex justify-center">
              <Countdown />
            </div>
          </div>
        ) : (
          <div className="mt-6 text-center">
            <p className="text-xs font-bold uppercase tracking-wide text-[#68677A]">
              Promocja zakończona
            </p>
            <p className="mt-1 text-5xl font-extrabold tracking-tight text-[#242344]">399 zł</p>
            <p className="mt-1 text-xs font-semibold text-[#68677A]">brutto</p>
          </div>
        )}

        <div className="mt-auto pt-6">
          <BuyButton testid="final-offer-buy-button" location="offer" className="w-full" />
        </div>
      </div>
    </div>
  );
}

function PackageCard({ pkg }) {
  return (
    <div
      data-testid={`package-card-${pkg.id}`}
      className={`relative flex h-full flex-col overflow-hidden rounded-3xl bg-white shadow-xl shadow-[#242344]/5 transition-all duration-300 hover:-translate-y-2 hover:shadow-2xl hover:shadow-[#FF008B]/20 ${
        pkg.highlight
          ? "border-2 border-[#FF008B] hover:shadow-[#FF008B]/30"
          : "border border-[#EAE6F0] hover:ring-2 hover:ring-[#FFB3DA]"
      }`}
    >
      {pkg.highlight && (
        <span className="absolute right-4 top-4 z-10 rounded-full bg-[#FF008B] px-3 py-1 text-xs font-extrabold uppercase tracking-wide text-white shadow-md">
          {pkg.highlight}
        </span>
      )}
      <div className="airmail-edge" aria-hidden="true" />
      <div className="bg-[#FAF8FC] p-6">
        <img
          src={pkg.image}
          alt={pkg.imageAlt}
          loading="lazy"
          className="mx-auto w-full max-w-[19rem] rounded-2xl"
        />
      </div>
      <div className="flex flex-1 flex-col p-7">
        <p className="text-center text-xs font-extrabold uppercase tracking-[0.25em] text-[#FF008B]">
          {pkg.badge}
        </p>
        <h3 className="mt-2 text-center text-xl font-bold text-[#242344]">{pkg.name}</h3>
        <ul className="mt-5 space-y-3">
          {pkg.items.map((item) => (
            <PackageItem key={item.label} item={item} pkgId={pkg.id} />
          ))}
        </ul>
        <div className="mt-6 text-center">
          <PricePromo regular={pkg.regular} price={pkg.price} note="brutto" />
          <p className="mt-2.5 text-sm font-bold text-[#242344]">Oszczędzasz {pkg.savings}</p>
        </div>
        <div className="mt-auto pt-6">
          {pkg.href ? (
            <a
              href={pkg.href}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`package-buy-${pkg.id}`}
              onClick={() => trackPackageClick(pkg)}
              className="inline-flex min-h-[48px] w-full items-center justify-center rounded-full bg-[#242344] px-8 py-3 text-base font-bold text-white transition-all duration-200 hover:-translate-y-0.5 hover:bg-[#FF008B]"
            >
              {pkg.cta}
            </a>
          ) : (
            <span
              data-testid={`package-buy-${pkg.id}`}
              className="inline-flex min-h-[48px] w-full cursor-not-allowed items-center justify-center rounded-full bg-[#EAE6F0] px-8 py-3 text-base font-bold text-[#68677A]"
              aria-disabled="true"
            >
              {pkg.cta}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

export default function Offer() {
  const { active } = usePromotion();

  return (
    <section id="cena" className="bg-white py-16 sm:py-24">
      <div className="mx-auto max-w-6xl px-4 sm:px-6">
        <Reveal>
          <h2 className="text-center text-2xl font-bold tracking-tight text-[#242344] sm:text-3xl lg:text-4xl">
            Wybierz swój pakiet
          </h2>
          <p className="mx-auto mt-3 max-w-2xl text-center text-base text-[#34343F] sm:text-lg">
            Sam kurs albo kurs z Pigułkami Wiedzy w pakiecie
          </p>
        </Reveal>
        <div className="mt-10 grid items-stretch gap-6 md:grid-cols-2 lg:grid-cols-3">
          <Reveal className="h-full">
            <SoloCard active={active} />
          </Reveal>
          {PACKAGES.map((pkg, i) => (
            <Reveal key={pkg.id} delay={(i + 1) * 0.08} className="h-full">
              <PackageCard pkg={pkg} />
            </Reveal>
          ))}
        </div>
        <Reveal>
          <div className="mt-8 text-center">
            <p className="text-xs text-[#68677A]">*Podane kwoty są cenami brutto</p>
            <p className="mt-3 text-sm text-[#34343F]">
              Kupując bilet, akceptujesz{" "}
              <a
                href={REGULAMIN_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="offer-regulamin-link"
                className="font-bold text-[#FF008B] underline underline-offset-2 hover:text-[#D90077]"
              >
                Regulamin
              </a>
              .
            </p>
            <p className="mt-3 text-xs text-[#68677A]">
              *Najniższa cena kursu w ciągu ostatnich 30 dni wyniosła 399 PLN brutto
            </p>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
